const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');

/**
 * Keys that edit, or move around within, the text being typed. A scanner only ever sends the characters of the code
 * (and the return 'Enter' key at the end), so any of these can only have come from a person.
 */
const EDITING_KEY_CODES = [
    8, // Backspace
    35, // End
    36, // Home
    37, // Left arrow
    38, // Up arrow
    39, // Right arrow
    40, // Down arrow
    46, // Delete
];

/**
 * @constructor
 *
 * Both barcode scans and RFID scans are basically a virtual keyboard typing in the barcode quickly then pressing enter.
 * We listen for these on ProPoint by listening for keydown events in quick succession. If the "typing" was fast enough
 * and ends with the enter key being pressed that is interpreted as either a barcode scan or an RFID scan, depending
 * on which Regex pattern is matched.
 *
 * @param $modalStack
 * @param $rootScope
 * @param $timeout
 * @param {VueModalFactory} vueModalFactory
 */
const KeyboardInputDetector = function (
    $modalStack,
    $rootScope,
    $timeout,
    vueModalFactory
) {
    this.$modalStack = $modalStack;
    this.$rootScope = $rootScope;
    this.$timeout = $timeout;
    this.vueModalFactory = vueModalFactory;

    this.defaults = {
        maxTimeBetweenInputs: 200, // time in milliseconds to wait between *each character* input from the scanner
        maxAverageTimeBetweenInputs: 50, // average ms between characters, faster than a person can type
        debug: false, // send debugging output to browser console
        suppressReturnEvent: true, // stop the 'return' character from submitting forms etc, when a scan occurs
        suppressKeyEvent: true, // stop characters being displayed in input elements when a scan occurs
    };

    // Mapping of events to fire with their respective regular expressions to trigger them.
    this.patterns = {};

    this.options = {
        ...this.defaults,
    };
    // Stored as a DOMHighResTimeStamp (see keyDownReceived), not a Date, so it can be
    // compared directly against event.timeStamp.
    this.lastInputAt = performance.now();
    // Timestamps of the first and most recent *characters* in the buffer (the return key that ends a scan is not
    // a character), used to measure how fast they arrived.
    this.bufferStartedAt = performance.now();
    this.lastCharacterAt = performance.now();
    this.buffer = '';
    // Characters dropped from the buffer the last time a gap reset it, so
    // reconcileWithFocusedInput can recognise a reset that happened mid-scan.
    this.discardedBuffer = '';

    this.log = logFactory('KeyboardInputDetector', this.options.debug);

    /**
     * @type {{string:string,rawString:string, time:Date, eventName:string}[]}
     */
    this.recentScans = [];
};

KeyboardInputDetector.prototype = {
    /**
     * Adds an event listener to the document to start listening for barcode scan events.
     *
     * @param {Object.<string, string>} patternEvents Object of which event names to broadcast for which regexs scanned.
     */
    initialize(patternEvents) {
        this.log.log('Initialising BarcodeScanDetector...', this.options);

        Object.keys(patternEvents).forEach((pattern) => {
            this.addPattern(patternEvents[pattern], pattern);
        });

        document.addEventListener('keydown', (event) => {
            this.keyDownReceived(event);
        });
    },

    /**
     * @param {string} event
     * @param {string} regex
     */
    addPattern(event, regex) {
        this.patterns[event] = regex;
    },

    /**
     * @param {KeyboardEvent} event
     */
    keyDownReceived(event) {
        // The event.target may be the document if a fake scan was performed in the console.
        // In this case event.target exists but does not have a .closest() method. So check that exists before calling.
        if (
            event.target
            && typeof event.target.closest === 'function'
            && event.target.closest('.no-scan-detection')
        ) {
            // This keydown event happened inside an element (likely a form) with the "no-scan-detection" class
            // so ignore the event.
            this.log.debug('Ignored keydown because the event is inside .no-scan-detection');
            return;
        }

        // Use the timestamp from the event rather than new Date()
        // event.timeStamp is set by the browser when the key event happens, so we
        // avoid any delay in the main thread caused by other activity like UI updates
        // (e.g. clearing or selecting the input field).
        // The problem with new Date() is that any delay between two
        // characters of the same scan could make it look like a much longer gap had
        // happened and this would cause the buffer to reset.
        let now = event.timeStamp;
        let delay = now - this.lastInputAt;

        this.log.debug('buffer: ' + this.buffer, 'delay: ' + delay);

        // Ignore the "SHIFT" keydown event (before we set the last input time).
        // There is no keyup event for "SHIFT", of course, because we don't listen for it.
        if (event.which === 16) {
            return;
        }

        this.lastInputAt = now;

        // A shortcut such as Ctrl+A. AltGr reports as Ctrl+Alt and types ordinary characters, so it is not one.
        const isShortcut = (event.ctrlKey || event.metaKey) && !event.altKey;

        if (isShortcut || EDITING_KEY_CODES.includes(event.which)) {
            // Someone is correcting or moving around what they have typed, so nothing buffered up to this point can
            // be part of a scan.
            this.log.debug('Editing key pressed; discarding the buffer');
            this.buffer = '';
            this.discardedBuffer = '';
            return;
        }

        if (delay < this.options.maxTimeBetweenInputs) {
            if (event.which === 13) {
                // the return key was pressed
                this.log.debug('Return key detected...');
                this.processString(this.buffer, event);
                this.buffer = '';
            } else {
                // this isn't the return key, but did occur very quickly after last input
                if (this.buffer === '') {
                    this.bufferStartedAt = now;
                }
                this.buffer += String.fromCharCode(event.which);
                this.lastCharacterAt = now;
            }
        } else {
            // there was a gap between input; clear the buffer and set it to the key that was pressed
            this.discardedBuffer = this.buffer;
            this.buffer = String.fromCharCode(event.which);
            this.bufferStartedAt = now;
            this.lastCharacterAt = now;
        }
    },

    /**
     * Check if the given string matches any of the expected pattens and fire the appropriate event if it does.
     *
     * @param {string} string
     * @param {KeyboardEvent} event
     */
    processString(string, event) {
        const rawString = string;
        string = this.reconcileWithFocusedInput(string);

        // Remove control characters from string:
        // \u0000-\u001F matches a single character in the range between NUL (index 0) and  (index 31)
        // \u007F-\u009F matches a single character in the range between  (index 127) and  (index 159)
        string = string.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');

        // Checked after removing control characters, because keys such as Tab put one in the buffer but not in the
        // field that looksLikeManualTyping compares against.
        if (this.looksLikeManualTyping(string)) {
            // Someone typing, not a scan. Return before anything is broadcast and before the return key is
            // suppressed, so their form submits as it normally would.
            this.log.debug(`Ignoring '${string}' because it was typed rather than scanned`);

            return;
        }

        this.log.debug('Processing string', string);
        for (let eventName in this.patterns) {
            if (this.patterns.hasOwnProperty(eventName)) {
                let pattern = this.patterns[eventName];
                let scanExp = new RegExp(pattern, 'i');
                if (string.match(scanExp)) {
                    // Suppress the default action of the input event if a barcode is detected
                    if (event && this.options.suppressReturnEvent) {
                        event.preventDefault();
                    }

                    this.log.log('Broadcasting event: ' + eventName);

                    // Check if any modal is currently open.
                    // TODO: This doesn't really belong here. It should be up to the consumer to check for
                    // active modals and proceed accordingly.
                    let hasOpenModal = this.$modalStack.getTop() || this.vueModalFactory.countActiveModals() > 0;

                    this.$timeout(() => {
                        const time = new Date();

                        const eventDetail = {
                            code: string,
                            hasOpenModal,
                            time,
                        };

                        // Broadcast the event on Angular's $rootScope.
                        this.$rootScope.$broadcast(eventName, eventDetail);

                        // Broadcast as a custom event on the document.
                        if (typeof document !== 'undefined') {
                            document.dispatchEvent(new CustomEvent(
                                eventName,
                                {
                                    detail: eventDetail,
                                }
                            ));
                        }

                        this.recentScans.unshift({
                            string,
                            rawString,
                            time,
                            eventName,
                        });
                        // Keep only the last 5 scans.
                        this.recentScans.splice(5);
                    });
                }
            }
        }
    },

    /**
     * The buffer built up in keyDownReceived is tracked completely independently of
     * whatever the currently focused input element's own value is (that's handled
     * natively by the browser, e.g. via a Vue v-model binding). Normally these agree.
     *
     * If a gap between two keys of the same scan resets the buffer (see
     * keyDownReceived), the leading characters end up in discardedBuffer while the
     * focused input still holds the whole scan. When the input's value is exactly the
     * discarded characters followed by the buffer, nothing else was in the field and
     * the two halves can safely be rejoined.
     *
     * The comparison is an exact match rather than a suffix test on purpose. A suffix
     * test cannot tell a mid-scan reset apart from a field that already had text in it
     * before the scan (someone typing a partial search, then scanning), and in that
     * case it returns the typed text as part of the scanned code.
     *
     * @param {string} bufferedString
     * @returns {string}
     */
    reconcileWithFocusedInput(bufferedString) {
        const el = document.activeElement;
        const isTextInput = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');

        if (!isTextInput || typeof el.value !== 'string') {
            // Nothing focused, or focus isn't on a text field - e.g. a USB scanner
            // used while no relevant input has focus. The buffer is all we have.
            return bufferedString;
        }

        const rejoined = this.discardedBuffer + bufferedString;

        if (
            bufferedString.length > 0
            && this.discardedBuffer.length > 0
            && el.value === rejoined
        ) {
            this.log.debug('Restoring characters lost from the buffer: "' + rejoined + '"');

            return rejoined;
        }

        return bufferedString;
    },

    /**
     * Typing quickly and pressing enter is, by content alone, indistinguishable from a scan: both are keydowns in
     * quick succession ending with the return key. Someone typing a name into a search box was therefore treated as
     * having scanned whichever fragment of it followed their last pause (see keyDownReceived), and the consumer of
     * the event then put that fragment back into the field they were typing in.
     *
     * The buffer is built from key codes, so it is always uppercase, whereas the browser puts the character actually
     * produced into the focused field. If that field ends with our characters but in a different case, a person
     * typed them: nobody was holding shift, and for the same characters to have been scanned in lowercase they would
     * have had to arrive faster than anyone can type.
     *
     * @param {string} string
     *
     * @return {boolean}
     */
    looksLikeManualTyping(string) {
        const el = document.activeElement;
        const isTextInput = el && ['INPUT', 'TEXTAREA'].includes(el.tagName);

        if (!isTextInput || typeof el.value !== 'string' || string.length === 0) {
            return false;
        }

        if (el.value.endsWith(string)) {
            // The field ends with exactly what we buffered, case and all, which is what a scan into a field looks
            // like (whether or not it already contained text).
            return false;
        }

        if (!el.value.toUpperCase().endsWith(string.toUpperCase())) {
            // The characters didn't go into this field, so it can't tell us whether they were typed.
            return false;
        }

        // The same characters in a different case.
        return !this.wasFastEnoughToBeScanned();
    },

    /**
     * Whether the characters in the buffer arrived at a speed a person could not type at.
     *
     * @return {boolean}
     */
    wasFastEnoughToBeScanned() {
        if (this.buffer.length < 2) {
            // A single character has no gaps to judge a rhythm from.
            return true;
        }

        const averageGap = (this.lastCharacterAt - this.bufferStartedAt) / (this.buffer.length - 1);

        this.log.debug(`Average gap between characters: ${averageGap} ms`);

        return averageGap < this.options.maxAverageTimeBetweenInputs;
    },

    /**
     * Returns info about the most recent scan, if one happened in the last 1000ms.
     *
     * @returns {{string: string, rawString: string, time: Date, eventName: string}|boolean}
     */
    hasRecentScan() {
        const now = (new Date().getTime());

        for (let i = 0; i < this.recentScans.length; i++) {
            const scan = this.recentScans[i];
            if (now - scan.time.getTime() < 1000) {
                // This string was scanned in the last 1000ms.
                return scan;
            }
        }

        return false;
    },
};

module.exports = KeyboardInputDetector;
